/**
 * Product sticky bar. 
 * 
 * All settings in the sticky bar are used only for presentation purposes. 
 * The ATC logic is handled by the options in the product section.
 * Here we just sync the options and transmit changes to the main product section.
 */
if (!customElements.get('product-sticky-bar')) {
  class ProductStickyBar extends HTMLElement {
    selectors = {
      mainContainer: '[data-section-type="product"]',
      buttonAtc: '[data-add-to-cart]',
      optionButtons: '[data-option-buttons] [data-button]',
      mainSelect: '[data-option-buttons] select',
      quantityInput: '[data-quantity-input]',
      quantityAdd: '[data-add-quantity]',
      quantitySubtract: '[data-subtract-quantity]',
      selectedValueNameEl: '[data-selected-value-for-option]',
      productOptionWrapper: '[data-product-option=""]',
      toggleSettingsBtn: '[data-toggle-settings]'
    }

    attributes = {
      optionHandle: 'data-option-handle'
    }
    
    constructor() {
      super();
    }

    connectedCallback() {
      // Main Product container elements:
      this.mainContainer = document.querySelector(this.selectors.mainContainer);
      this.mainATC = this.mainContainer?.querySelector(this.selectors.buttonAtc);
      this.mainSelect = this.mainContainer?.querySelector(this.selectors.mainSelect);
      this.mainOptions = [...this.mainContainer?.querySelectorAll(this.selectors.optionButtons)];
      this.mainQuantity = this.mainContainer?.querySelector('[data-quantity-input]');
      this.quantityAdd = this.mainContainer?.querySelector(this.selectors.quantityAdd);
      this.quantitySubtract = this.mainContainer?.querySelector(this.selectors.quantitySubtract);

      // Sticky bar elements:
      this.stickyOptions = [...this.querySelectorAll(this.selectors.optionButtons)];
      this.stickyAtc = this.querySelector(this.selectors.buttonAtc);
      this.stickySelect = this.querySelector(this.selectors.mainSelect);
      this.stickyQuantityInput = this.querySelector(this.selectors.quantityInput);
      this.stickyQuantityAdd = this.querySelector(this.selectors.quantityAdd);
      this.stickyQuantitySubtract = this.querySelector(this.selectors.quantitySubtract);
      this.toggleSettingsBtn = this.querySelector(this.selectors.toggleSettingsBtn);

      if (!this.mainContainer) {
        return;
      }

      this.handleStickyBarVisibility();
      this.quantitySelectorSync();
      this.handleStickyAtcClick();
      this.handleOptionsState();

      this.toggleSettingsBtn.addEventListener('click', ()=> {
        this.classList.toggle('is-open')
      })
    }

    /**
     * Handle sticky bar visibility.
     */
    handleStickyBarVisibility() {
      if (this.mainATC.getBoundingClientRect().y < 0) {
        this.classList.remove('is-hidden')
      }
      
      const observer = new IntersectionObserver(entries => {
        // [not visible in viewport] && [scrolled past]
        const showStickyBar = !entries[0].isIntersecting && this.mainATC.getBoundingClientRect().y < 0

        this.classList.toggle('is-hidden', !showStickyBar)
      });

      observer.observe(this.mainATC);
    }

    /**
     * Update sticky ATC state.
     */
    updateStickyAtcState() {
      this.stickyAtc.innerHTML = this.mainATC.innerHTML;
      this.stickyAtc.className = this.mainATC.className;

      this.stickyAtc.toggleAttribute('disabled', this.mainATC.hasAttribute('disabled'))
    }

    /**
     * Handle sticky ATC click.
     */
    handleStickyAtcClick() {
      const removeStickyLoading = () => this.stickyAtc.classList.remove('loading');

      document.addEventListener('cart:updated', removeStickyLoading);
      document.addEventListener('cart:item-added', removeStickyLoading);
      document.addEventListener('cart:error', removeStickyLoading);

      this.stickyAtc.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.mainATC?.click();
        this.stickyAtc.classList.add('loading');
      })
    }

    /**
     * Quantity selector sync.
     */
    quantitySelectorSync() {
      this.mainQuantity.addEventListener('change', () => {
        this.stickyQuantityInput.value = this.mainQuantity.value;
      })

      this.stickyQuantityAdd.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.quantityAdd?.click();
      })

      this.stickyQuantitySubtract.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.quantitySubtract?.click();
      })
    }

    /**
     * Handle options state.
     */
    handleOptionsState() {
      this.optionsMap = new Map();

      this.mainOptions.forEach(option => {
        const optionHandle = option.getAttribute(this.attributes.optionHandle);
        const stickyOption = this.stickyOptions.find(opt => {
          return optionHandle === opt.getAttribute(this.attributes.optionHandle)
        });

        // Group duplicate options into a map.
        this.optionsMap.set(optionHandle, {
          mainOption: option,
          stickyOption
        });

        this.updateStickyOption(option, optionHandle);

        // Attach events.
        option.addEventListener('click', () => this.handleOptionClick(option, optionHandle));
        stickyOption.addEventListener('click', () => this.handleStickyOptionClick(optionHandle));
      })
    }

    /**
     * Handle sticky option click.
     * @param {HTMLElement} option 
     * @param {String} handle 
     */
    handleStickyOptionClick(handle) {
      const mainOption = this.optionsMap.get(handle)?.mainOption;

      mainOption?.click();

      this.updateStickyAtcState();
    }

    /**
     * Handle main option click.
     * @param {HTMLElement} option 
     * @param {String} handle 
     */
    handleOptionClick(option, handle) {
      this.optionsMap.forEach(item => {
        this.updateStickyOption(item.mainOption);
      })

      this.updateStickyAtcState();
    }

    /**
     * Update sticky option.
     * @param {HTMLElement} option 
     * @param {String} handle 
     * @returns {Void}
     */
    updateStickyOption(option, handle) { 
      const _handle = handle || option.getAttribute(this.attributes.optionHandle);
      const stickyOption = this.optionsMap.get(_handle)?.stickyOption;

      if (!stickyOption) {
        return;
      }

      stickyOption.className = option.className;
      
      // Update option selected value text.
      const optionNameEl = stickyOption.closest(this.selectors.productOptionWrapper)?.querySelector(this.selectors.selectedValueNameEl);
      
      if (optionNameEl && stickyOption.classList.contains('selected')) {
        optionNameEl.innerText = stickyOption.getAttribute('data-option-value');
      }
    }
  }

  customElements.define('product-sticky-bar', ProductStickyBar);
}